import React, { useRef, useEffect, useCallback, useReducer, useLayoutEffect, forwardRef } from 'react';
import { lazy, Suspense } from '@uploadcare/client-suspense';
import isEqual from 'react-fast-compare';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var useCommitedRef = function useCommitedRef(value) {
  var ref = useRef(value);
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref;
};

var useEventCallback = function useEventCallback(callback) {
  var ref = useCommitedRef(callback);
  return useCallback(function () {
    return ref.current && ref.current.apply(ref, arguments);
  }, [ref]);
};

var merge = function merge(state, newState) {
  return _objectSpread2({}, state, {}, newState);
};

var useState = function useState(initial) {
  return useReducer(merge, initial || {});
};

var noop = function noop() {};

var useCustomTabs = function useCustomTabs(tabs, uploadcare) {
  useEffect(function () {
    var customTabs = Object.entries(tabs || []);
    customTabs.forEach(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
          name = _ref2[0],
          implementation = _ref2[1];

      uploadcare.registerTab(name, function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        return implementation.apply(void 0, args.concat([uploadcare]));
      });
    });
    return function () {
      return customTabs.forEach(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 1),
            name = _ref4[0];

        return uploadcare.registerTab(name, noop);
      });
    };
  }, [tabs, uploadcare]);
};

var useValidators = function useValidators(widget, validators) {
  useEffect(function () {
    if (validators != null) {
      var instance = widget.current;
      validators.forEach(function (validator) {
        instance.validators.push(validator);
      });
      return function () {
        instance.validators.length = 0;
      };
    }
  }, [widget, validators]);
};

function useDeepMemo(factory, deps) {
  var isValid = true;
  var valueRef = useRef(); // initial hook call

  if (!valueRef.current) {
    valueRef.current = {
      deps: deps,
      result: factory()
    }; // subsequent calls
  } else {
    isValid = !!(deps && valueRef.current.deps && isEqual(deps, valueRef.current.deps));
  }

  var cache = isValid ? valueRef.current : {
    deps: deps,
    result: factory()
  }; // must update immediately so any sync renders here don't cause an infinite loop

  valueRef.current = cache;
  return cache.result;
}

var isReactNative = typeof global !== 'undefined' && global.navigator && global.navigator.product === 'ReactNative';
var isDOM = typeof document !== 'undefined';
/**
 * Is `useLayoutEffect` in a DOM or React Native environment, otherwise resolves to useEffect
 * Only useful to avoid the console warning.
 *
 * PREFER `useEffect` UNLESS YOU KNOW WHAT YOU ARE DOING.
 */

var useIsomorphicEffect = isDOM || isReactNative ? useLayoutEffect : useEffect;
var Uploader = lazy(function () {
  return import(
  /* webpackChunkName: "ucare-widget-chunk" */
  './uploader-d6dc4a64.js');
});
var Dialog = lazy(function () {
  return import(
  /* webpackChunkName: "ucare-panel-chunk" */
  './dialog-34a23a00.js');
});

var Config = function Config(_ref) {
  var locale = _ref.locale,
      localeTranslations = _ref.localeTranslations,
      localePluralize = _ref.localePluralize;
  useIsomorphicEffect(function () {
    window.UPLOADCARE_INTEGRATION = 'react-widget';
    window.UPLOADCARE_LIVE = false;
    window.UPLOADCARE_MANUAL_START = true;
  }, []); // configurate translations

  useIsomorphicEffect(function () {
    window.UPLOADCARE_LOCALE = window.UPLOADCARE_LOCALE || locale;
    window.UPLOADCARE_LOCALE_TRANSLATIONS = window.UPLOADCARE_LOCALE_TRANSLATIONS || localeTranslations;
    window.UPLOADCARE_LOCALE_PLURALIZE = window.UPLOADCARE_LOCALE_PLURALIZE || localePluralize; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

var Widget = forwardRef(function (_ref2, ref) {
  var locale = _ref2.locale,
      localeTranslations = _ref2.localeTranslations,
      localePluralize = _ref2.localePluralize,
      _ref2$preloader = _ref2.preloader,
      preloader = _ref2$preloader === void 0 ? 'Loading...' : _ref2$preloader,
      props = _objectWithoutProperties(_ref2, ["locale", "localeTranslations", "localePluralize", "preloader"]);

  return React.createElement(React.Fragment, null, React.createElement(Config, {
    locale: locale,
    localeTranslations: localeTranslations,
    localePluralize: localePluralize
  }), React.createElement(Suspense, {
    fallback: preloader
  }, React.createElement(Uploader, _extends({}, props, {
    apiRef: ref
  }))));
});

var Panel = function Panel(_ref3) {
  var locale = _ref3.locale,
      localeTranslations = _ref3.localeTranslations,
      localePluralize = _ref3.localePluralize,
      _ref3$preloader = _ref3.preloader,
      preloader = _ref3$preloader === void 0 ? 'Loading...' : _ref3$preloader,
      props = _objectWithoutProperties(_ref3, ["locale", "localeTranslations", "localePluralize", "preloader"]);

  return React.createElement(React.Fragment, null, React.createElement(Config, {
    locale: locale,
    localeTranslations: localeTranslations,
    localePluralize: localePluralize
  }), React.createElement(Suspense, {
    fallback: preloader
  }, React.createElement(Dialog, props)));
};

export { Panel as P, Widget as W, _extends as _, _objectWithoutProperties as a, useCustomTabs as b, useDeepMemo as c, useValidators as d, _slicedToArray as e, _objectSpread2 as f, _defineProperty as g, useState as h, useEventCallback as u };